import { motion } from "framer-motion";
import { Card, CardContent } from "@/components/ui/card";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Quote, ChevronLeft, ChevronRight, Play, Pause } from "lucide-react";
import { useState, useEffect, useRef } from "react";

const testimonials = [
  {
    id: 1,
    name: "Jason Martis",
    role: "Marketing Manager",
    feedback: "200% revenue increase thanks to MaxiPitch",
    image: "https://randomuser.me/api/portraits/men/32.jpg",
    stars: 5,
  },
  {
    id: 2,
    name: "Priya",
    role: "Business Owner",
    feedback: "The easiest website to use. Just love it",
    image: "https://randomuser.me/api/portraits/women/8.jpg", // Indian
    stars: 4,
  },
  {
    id: 3,
    name: "Sonia Sharma",
    role: "Sales Director",
    feedback: "Got 8 new clients for our IT Staffing business in just 2 months",
    image: "https://randomuser.me/api/portraits/women/15.jpg", // Indian
    stars: 5,
  },
  {
    id: 4,
    name: "Alex Lee",
    role: "Startup Founder",
    feedback:
      "I now receive new client enquiries almost everyday, completed automated.",
    image: "https://randomuser.me/api/portraits/men/22.jpg",
    stars: 4,
  },
  {
    id: 5,
    name: "Sophia Brown",
    role: "Freelancer",
    feedback:
      "I can now manage multiple clients easily and track leads in real-time. Amazing tool!",
    image: "https://randomuser.me/api/portraits/women/10.jpg",
    stars: 5,
  },
  {
    id: 6,
    name: "Michael Green",
    role: "Digital Marketer",
    feedback:
      "Lead generation campaigns are easier than ever. My ROI improved significantly.",
    image: "https://randomuser.me/api/portraits/men/18.jpg",
    stars: 4,
  },

  {
    id: 7,
    name: "Robert Chen",
    role: "Growth Hacker",
    feedback:
      "Integration with our CRM was seamless. This platform has become an essential part of our growth strategy.",
    image: "https://randomuser.me/api/portraits/men/45.jpg",
    stars: 4,
  },

  // ⭐ Added Indian Feedback #1
  {
    id: 8,
    name: "Rohit Verma",
    role: "IT Consultant",
    feedback:
      "MaxiPitch helped us reduce lead acquisition time by 60%. Highly recommended for Indian businesses.",
    image: "https://randomuser.me/api/portraits/men/51.jpg", // Indian
    stars: 4,
  },

  // ⭐ Added Indian Feedback #2
  {
    id: 9,
    name: "Neha Kulkarni",
    role: "Agency Owner",
    feedback:
      "Amazing platform! I closed 5 new clients this month using the automated outreach system.",
    image: "https://randomuser.me/api/portraits/women/56.jpg", // Indian
    stars: 5,
  },
];

export default function TestimonialGrid() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const scrollContainerRef = useRef(null);
  const scrollIntervalRef = useRef(null);

  const scrollToCard = (index) => {
    if (scrollContainerRef.current) {
      const container = scrollContainerRef.current;
      const cardWidth = container.children[0]?.offsetWidth || 0;
      const gap = 24; // gap-6 = 24px
      const scrollPosition = index * (cardWidth + gap);

      container.scrollTo({
        left: scrollPosition,
        behavior: "smooth",
      });
    }
  };

  const handleNext = () => {
    const nextIndex = (currentIndex + 1) % testimonials.length;
    setCurrentIndex(nextIndex);
    scrollToCard(nextIndex);
  };

  const handlePrev = () => {
    const prevIndex =
      (currentIndex - 1 + testimonials.length) % testimonials.length;
    setCurrentIndex(prevIndex);
    scrollToCard(prevIndex);
  };

  // Auto-scroll every 3 seconds
  useEffect(() => {
    scrollIntervalRef.current = setInterval(() => {
      handleNext();
    }, 3000);

    return () => clearInterval(scrollIntervalRef.current);
  }, [currentIndex]);

  return (
    <section className=" text-center text-black relative overflow-hidden">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -30 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="mb-12 px-6"
      >
        <span className="px-4 py-1 rounded-full bg-gray-200 text-base font-medium">
          Testimonials
        </span>
        <h2 className="text-4xl font-bold mt-6 text-gray-900">
          What Our Clients Say About Maxipitch- Lead Generation
        </h2>
        <p className="mt-4 text-gray-600 max-w-2xl mx-auto">
          Our AI-powered lead generation platform helps businesses capture,
          nurture, and convert leads effortlessly. See what our happy customers
          say about their journey with us.
        </p>
      </motion.div>

      {/* Testimonial Grid */}
      <div className="relative flex justify-center items-center w-full">
        {/* Left button */}
        <button
          onClick={handlePrev}
          className="absolute left-0 top-1/2 -translate-y-1/2 z-10 p-3 rounded-full bg-gray-100 hover:bg-gray-200 shadow"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>

        {/* Right button */}
        <button
          onClick={handleNext}
          className="absolute right-0 top-1/2 -translate-y-1/2 z-10 p-3 rounded-full bg-gray-100 hover:bg-gray-200 shadow"
        >
          <ChevronRight className="w-5 h-5" />
        </button>

        <div
          ref={scrollContainerRef}
          className="flex justify-center md:justify-start overflow-x-auto scrollbar-hide gap-6 pb-8"
          style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
        >
          {testimonials.map((t, index) => (
            <motion.div
              key={t.id}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: index * 0.1 }}
              whileHover={{ scale: 1.05 }}
              className="flex-shrink-0 w-80 snap-start mx-auto md:mx-0"
            >
              <Card className="bg-gray-50 border border-gray-200 rounded-2xl shadow-md hover:shadow-xl transition-shadow h-full">
                <CardContent className="p-4 flex flex-col justify-between h-full">
                  <div className="flex text-yellow-500 mb-4">
                    {"★".repeat(t.stars)}
                  </div>
                  <p className="text-gray-700 mb-4 text-sm leading-relaxed">
                    "{t.feedback}"
                  </p>
                  <div className="flex items-center justify-between mt-auto">
                    <div className="flex items-center space-x-3">
                      <Avatar>
                        <AvatarImage src={t.image} alt={t.name} />
                        <AvatarFallback>{t.name[0]}</AvatarFallback>
                      </Avatar>
                      <div className="text-left">
                        <h4 className="font-semibold text-base text-gray-900">
                          {t.name}
                        </h4>
                        <p className="text-sm text-gray-500">{t.role}</p>
                      </div>
                    </div>
                    <Quote className="text-gray-400 w-5 h-5" />
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>

        {/* Gradient fade effects */}
        <div className="pointer-events-none absolute inset-y-0 left-0 w-20 bg-gradient-to-r from-white to-transparent"></div>
        <div className="pointer-events-none absolute inset-y-0 right-0 w-20 bg-gradient-to-l from-white to-transparent"></div>
      </div>

      {/* Dots indicator */}
      <div className="flex justify-center mt-6 gap-2">
        {testimonials.map((_, index) => (
          <button
            key={index}
            onClick={() => {
              setCurrentIndex(index);
              scrollToCard(index);
            }}
            className={`w-2 h-2 rounded-full transition-colors ${
              index === currentIndex ? "bg-blue-500" : "bg-gray-300"
            }`}
          />
        ))}
      </div>
    </section>
  );
}
